"use client";

import { ComponentProps } from "react";
import { Tooltip as RadixTooltip } from "@radix-ui/themes";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { useIsShopsite } from "@context/DomainContext";
import { joinClassNames } from "@utils/helpers";

interface Props {
  children: React.ReactNode;
  className?: string;
  shopsiteClasses?: string;
  whitelabelClasses?: string;
  arrowClasses?: string;
  shopsiteContentProps?: React.ComponentPropsWithoutRef<
    typeof TooltipPrimitive.Content
  >;
}

export default function Tooltip({
  children,
  className = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  arrowClasses = "fill-indigo-100",
  shopsiteContentProps,
  content,
  side,
  open,
  ref,
  ...props
}: ComponentProps<typeof RadixTooltip> & Props) {
  const isShopsite = useIsShopsite();

  const shopsiteBaseClasses =
    "bg-indigo-100 rounded-lg p-4 text-indigo-800 relative z-50 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";

  if (!shopsiteContentProps?.sideOffset) {
    shopsiteContentProps = {
      ...shopsiteContentProps,
      sideOffset: 10,
    };
  }
  if (!shopsiteContentProps?.side) {
    shopsiteContentProps = { ...shopsiteContentProps, side };
  }

  return isShopsite ? (
    <TooltipPrimitive.Provider {...props}>
      <TooltipPrimitive.Root open={open}>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            className={joinClassNames(
              shopsiteBaseClasses,
              className,
              shopsiteClasses
            )}
            {...shopsiteContentProps}
          >
            {content}
            <TooltipPrimitive.Arrow className={arrowClasses} />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  ) : (
    <RadixTooltip
      ref={ref}
      content={content}
      className={joinClassNames(className, whitelabelClasses)}
      side={side}
      open={open}
      {...props}
    >
      {children}
    </RadixTooltip>
  );
}
